import { ProfileForm } from "@/components/Complex/ProfileForm"
import { useConfig } from "@/hooks/useConfig"
import { useNavigate, useSearchParams } from "react-router-dom"
import { useState } from "react"
import { z } from "zod/v4"
import { useToast } from "@/hooks/useToast"
import { Toaster } from "@/components/Primitives/Sonner/sonner"
import { createUser } from "@/lib/api/signup"
import { getPasswordSchema, getPasswordMessages } from "@/lib/schemas/auth"
import { getPasswordValidation } from "@/lib/utils/utils"
import { saveToken } from "@/hooks/useLocalStorage"
import { setToken } from "@/lib/api/client"
import { createCircle } from "@/lib/api/circle"

export function SignupProfilePage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const registrationCode = searchParams.get("registration_code")
  const { t } = useConfig()
  const [fullName, setFullName] = useState("")
  const [password, setPassword] = useState("")
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState("")
  const [fieldErrors, setFieldErrors] = useState<{
    fullName?: string
    password?: string
  }>({})
  const { success } = useToast()

  const passwordValidation = getPasswordValidation(password, t)
  const passwordSchema = getPasswordSchema(t)
  const isPasswordValid = passwordSchema.safeParse(password).success

  const handleSubmit = async () => {
    try {
      setError("")
      setFieldErrors({})

      if (!registrationCode) {
        setError(t("config_sign_up_profile_error_no_code"))
        return
      }

      const nameSchema = z
        .string()
        .min(1, t("config_sign_up_profile_error_name_short"))
        .max(100, t("config_sign_up_profile_error_name_long"))

      const passwordResult = passwordSchema.safeParse(password)
      const nameResult = nameSchema.safeParse(fullName)

      const newFieldErrors: {
        fullName?: string
        password?: string
      } = {}

      if (!nameResult.success) {
        const issues = nameResult.error.issues
        newFieldErrors.fullName = issues[0]?.message
      }

      if (!passwordResult.success) {
        const issues = passwordResult.error.issues
        newFieldErrors.password = issues[0]?.message
      }

      if (Object.keys(newFieldErrors).length > 0) {
        setFieldErrors(newFieldErrors)
        return
      }

      setIsSubmitting(true)

      const token = await createUser(registrationCode, fullName, password)
      saveToken(token)
      setToken(token)

      await createCircle(fullName)

      navigate("/signup/onboarding")
      success(t("config_sign_up_profile_success"))
    } catch (err) {
      if (err instanceof Error) {
        console.error("Profile creation failed:", err.message)
      } else {
        console.error("Profile creation failed:", err)
      }
      setError(t("config_sign_up_profile_error_generic"))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <>
      <Toaster />
      <ProfileForm
        fullName={fullName}
        password={password}
        onFullNameChange={setFullName}
        onPasswordChange={setPassword}
        onSubmit={handleSubmit}
        isSubmitting={isSubmitting}
        error={error}
        fieldErrors={fieldErrors}
        passwordValidation={passwordValidation}
        passwordRequirements={getPasswordMessages(t).map((label) => ({
          label,
        }))}
        isPasswordValid={isPasswordValid}
      />
    </>
  )
}
